// Third-party library imports
import {
    createAction,
    createSlice,
    PayloadAction
} from '@reduxjs/toolkit';

// Local imports - Constants
import type { RootState } from '../storeTypes';

// Local imports - Project-specific modules - components (Types / Interfaces)
import {
    AccessToken,
    AccessTokensState
} from './types';
import {
    DynamicObject,
    ResponseModalObject
} from '../../utils/commonInterfaces';

// Type-safe initialState!
export const initialState: AccessTokensState = {
    loading: false,
    errorMessages: [],
    revokeAccessTokensLoading: false,
    data: [],
};

const accessTokenSlice = createSlice({
    name: 'accessToken',
    initialState,
    reducers: {
        getAccessTokensRequestAction: (
            state
        ) => {
            state.loading = true;
        },
        getAccessTokensSuccessAction: (
            state,
            action: PayloadAction<{
                data: AccessToken[];
            }>
        ) => {
            state.loading = false;
            state.data = action.payload.data;
            state.errorMessages = [];
        },
        getAccessTokensErrorAction: (
            state,
            action: PayloadAction<{
                errorMessages: string[];
            }>
        ) => {
            state.loading = false;
            state.data = [];
            state.errorMessages = action.payload.errorMessages;
        },
        revokeAccessTokensRequestAction: {
            reducer: (
                state,
                action: PayloadAction<{
                    key: string;
                    callback?: (response: ResponseModalObject) => void;
                }>
            ) => {
                state.revokeAccessTokensLoading = true;
            },
            prepare: (
                key: string,
                callback?: (response: ResponseModalObject) => void
            ) => ({
                payload: { key, callback }
            })
        },
        revokeAccessTokensResponseAction: (
            state
        ) => {
            state.revokeAccessTokensLoading = false;
        },
        clearAccessTokensStateDataAction: (state) => {
            Object.assign(state, initialState);
        },
    }
});

const { actions, reducer } = accessTokenSlice;

// Actions
export const {
    getAccessTokensRequestAction,
    getAccessTokensSuccessAction,
    getAccessTokensErrorAction,
    revokeAccessTokensRequestAction,
    revokeAccessTokensResponseAction,
    clearAccessTokensStateDataAction
} = actions;

export const createAccessTokenRequestAction = createAction('accessToken/createAccessTokenRequestAction',
    function prepare(
        payload: DynamicObject,
        callback?: (response: DynamicObject) => void
    ) {
        return {
            payload: { payload, callback }
        };
    }
);

// Selectors
export const getAccessTokensState = (state: RootState) => state.accessTokens;

// Reducer
export default reducer;